import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, patchData, postData } from '../../services/request'

const InfoRow = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-3 text-sm">
    <div className="col-span-1 font-medium text-gray-600">{label}</div>
    <div className="col-span-2 text-gray-900">{value || '—'}</div>
  </div>
)

const AdminJobApprovalDetails = () => {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actioning, setActioning] = useState(false)
  const [aiReviewing, setAiReviewing] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const fetchJob = async () => {
    setLoading(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/jobs/${jobId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === '') {
      if (res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setJob(data)
      } else {
        setError(res.result?.data?.message || res.result?.data?.data || 'Job not found')
      }
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Network error')
    }
  }

  useEffect(() => {
    fetchJob()
  }, [jobId])

  const handleApprove = async () => {
    setActioning(true)
    const url = `${getAPIBaseURL()}/employme/admin/jobs/${jobId}/status`
    const headers = { 'Content-Type': 'application/json' }
    const res = await patchData(url, headers, { status: 'LIVE' })
    setActioning(false)
    if (res.error === '' && res.result?.status === 200) {
      navigate('/admin/app/employme/job-approvals')
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to approve')
    }
  }

  const handleAiReview = async () => {
    setAiReviewing(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/jobs/${jobId}/ai-review`
    const headers = { 'Content-Type': 'application/json' }
    const res = await postData(url, headers, {})
    setAiReviewing(false)
    if (res.error === '' && (res.result?.status === 200 || res.result?.status === 201)) {
      await fetchJob()
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'AI review failed')
    }
  }

  const handleReject = async () => {
    if (!rejectReason.trim()) return
    setActioning(true)
    const url = `${getAPIBaseURL()}/employme/admin/jobs/${jobId}/status`
    const headers = { 'Content-Type': 'application/json' }
    const res = await patchData(url, headers, { status: 'REJECTED', reason: rejectReason.trim() })
    setActioning(false)
    setShowRejectModal(false)
    setRejectReason('')
    if (res.error === '' && res.result?.status === 200) {
      navigate('/admin/app/employme/job-approvals')
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to reject')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso || '—'
    }
  }

  const formatCurrency = (amount) => {
    if (amount == null || amount === undefined) return '—'
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount)
  }

  const employerName = (j) => {
    const u = j.createdByUserId
    if (!u) return '—'
    return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '—'
  }

  if (loading) {
    return (
      <section className="mx-auto max-w-4xl">
        <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
      </section>
    )
  }

  if (error || !job) {
    return (
      <section className="mx-auto max-w-4xl">
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error || 'Job not found'}
        </div>
        <Link
          to="/admin/app/employme/job-approvals"
          className="inline-block rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to Job Approvals
        </Link>
      </section>
    )
  }

  return (
    <section className="mx-auto max-w-4xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Link
          to="/admin/app/employme/job-approvals"
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          ← Back to Job Approvals
        </Link>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleAiReview}
            disabled={aiReviewing || actioning}
            className="rounded-lg border border-indigo-600 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
          >
            {aiReviewing ? 'Reviewing...' : 'Run AI Review'}
          </button>
          <button
            onClick={handleApprove}
            disabled={actioning}
            className="rounded-lg border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {actioning ? 'Processing...' : 'Approve'}
          </button>
          <button
            onClick={() => setShowRejectModal(true)}
            disabled={actioning}
            className="rounded-lg border border-red-600 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            Reject
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-xl font-bold text-gray-900">{job.title}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {job.employmentType} • {job.locationType} • {formatCurrency(job.paymentAmount)} paid
          </p>
        </div>
        <div className="divide-y divide-gray-100 px-6">
          <InfoRow label="Status" value={job.status} />
          <InfoRow
            label="AI Moderation"
            value={
              job.moderation?.status
                ? `${job.moderation.status}${job.moderation.decision ? ` (${job.moderation.decision})` : ''}`
                : '—'
            }
          />
          {job.moderation?.reasons?.length > 0 && (
            <div className="border-b border-gray-100 py-3 text-sm">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-1 font-medium text-gray-600">AI Reasons</div>
                <div className="col-span-2">
                  <ul className="list-inside list-disc space-y-1 text-gray-800">
                    {job.moderation.reasons.map((r, idx) => (
                      <li key={idx}>{r}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <InfoRow label="Company" value={job.companyId?.name} />
          <InfoRow label="Employer" value={employerName(job)} />
          <InfoRow label="Employer Email" value={job.createdByUserId?.email} />
          <InfoRow label="Contact Person" value={job.companyId?.contactPersonName} />
          <InfoRow label="Contact Email" value={job.companyId?.contactEmail || job.createdByUserId?.email} />
          <InfoRow label="Contact Phone" value={job.companyId?.contactPhone || job.createdByUserId?.phoneNumber} />
          <InfoRow label="Department" value={job.department} />
          <InfoRow label="Employment Type" value={job.employmentType} />
          <InfoRow label="Location Type" value={job.locationType} />
          <InfoRow
            label="Location"
            value={
              job.location
                ? [job.location.addressLine, job.location.city, job.location.state, job.location.country]
                    .filter(Boolean)
                    .join(', ') || '—'
                : '—'
            }
          />
          <InfoRow
            label="Salary Range"
            value={
              job.salaryRange
                ? `${formatCurrency(job.salaryRange.min)} – ${formatCurrency(job.salaryRange.max)} ${job.salaryRange.currency || 'NGN'}`
                : '—'
            }
          />
          <InfoRow label="Salary Visible to Candidates" value={job.salaryRange?.visibleToCandidates ? 'Yes' : 'No'} />
          <InfoRow label="Application Deadline" value={formatDate(job.applicationDeadline)} />
          <InfoRow label="Applicants" value={job.applicantCount ?? 0} />
          <InfoRow label="Payment Reference" value={job.paymentTxRef} />
          <InfoRow label="Payment Amount" value={formatCurrency(job.paymentAmount)} />
          <InfoRow label="Payment Status" value={job.paymentStatus} />
          <InfoRow label="Submitted" value={formatDate(job.createdAt)} />
          <InfoRow label="Last Updated" value={formatDate(job.updatedAt)} />
        </div>
        <div className="border-t border-gray-200 px-6 py-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Description</h3>
          <div className="whitespace-pre-wrap text-sm text-gray-700">{job.description}</div>
        </div>
        <div className="border-t border-gray-200 px-6 py-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Requirements</h3>
          <div className="whitespace-pre-wrap text-sm text-gray-700">{job.requirements}</div>
        </div>
        {job.skills?.length > 0 && (
          <div className="border-t border-gray-200 px-6 py-4">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {job.skills.map((s, i) => (
                <span key={i} className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700">
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
        {job.benefits?.length > 0 && (
          <div className="border-t border-gray-200 px-6 py-4">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Benefits</h3>
            <ul className="list-inside list-disc space-y-1 text-sm text-gray-700">
              {job.benefits.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="border-t border-gray-200 px-6 py-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Interview Configuration</h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              Time limit: {job.interviewTimeLimitSeconds ?? 60} seconds per question
            </p>
            <p>
              Max takes: {job.interviewMaxTakes ?? 1} take(s) per question
            </p>
          </div>
        </div>
        {job.interviewQuestions?.length > 0 && (
          <div className="border-t border-gray-200 px-6 py-4">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Interview Questions</h3>
            <ol className="list-inside list-decimal space-y-2 text-sm text-gray-700">
              {job.interviewQuestions.map((q, i) => (
                <li key={i} className="pl-2">
                  {q}
                </li>
              ))}
            </ol>
          </div>
        )}
        {job.screening && (job.screening.requiredDocuments?.length > 0 || job.screening.eligibilityQuestions?.length > 0) && (
          <div className="border-t border-gray-200 px-6 py-4">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">Screening</h3>
            <div className="space-y-3">
              {job.screening.requiredDocuments?.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-600">Required Documents</p>
                  <ul className="list-inside list-disc space-y-0.5 text-sm text-gray-700">
                    {job.screening.requiredDocuments.map((d, i) => (
                      <li key={i}>
                        {d.label || 'Document'}
                        {d.isMandatory ? ' (mandatory)' : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {job.screening.eligibilityQuestions?.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-600">Eligibility Questions</p>
                  <ol className="list-inside list-decimal space-y-1 text-sm text-gray-700">
                    {job.screening.eligibilityQuestions.map((eq, i) => (
                      <li key={i}>
                        {eq.question}
                        {eq.type && eq.type !== 'YES_NO' ? ` (${eq.type})` : ''}
                        {eq.options?.length > 0 && (
                          <span className="text-gray-500"> — Options: {eq.options.join(', ')}</span>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-3 text-lg font-semibold text-gray-900">Reject job listing</h3>
            <p className="mb-3 text-sm text-gray-600">Please provide a reason for rejection (required):</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Job description does not meet platform guidelines..."
              rows={4}
              className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowRejectModal(false); setRejectReason('') }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="rounded-lg border border-red-600 bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default AdminJobApprovalDetails
